import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { setAccessToken } from '@/lib/auth'
import type { TenantRole, UpdateRoleInput } from '@/types/role'

const KEYS = {
  list: ['roles'] as const,
  detail: (id: string) => ['roles', id] as const,
}

async function refreshAccessTokenIfPossible() {
  try {
    const { data } = await api.post<{ accessToken: string; primaryColor: string }>(
      '/auth/refresh',
    )
    if (data?.accessToken) {
      setAccessToken(data.accessToken)
    }
  } catch {
    // Sem cookie de refresh disponível ou falha — ignora.
  }
}

export function useRoles() {
  return useQuery({
    queryKey: KEYS.list,
    queryFn: () => api.get<TenantRole[]>('/roles').then((r) => r.data),
  })
}

export function useRole(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => api.get<TenantRole>(`/roles/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

export function useCreateRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateRoleInput) =>
      api.post<TenantRole>('/roles', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  })
}

export function useUpdateRole(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateRoleInput) =>
      api.put<TenantRole>(`/roles/${id}`, data).then((r) => r.data),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['roles'] })
      // O usuário logado pode ter editado a própria função; o refresh re-emite
      // o JWT com as novas permissões, evitando logout/login.
      await refreshAccessTokenIfPossible()
    },
  })
}

export function useDeleteRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/roles/${id}`).then(() => undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] }),
  })
}
